import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';

import { catchAll } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { MyClubPageHeader } from '@sorare/core/src/components/navigation/MyClubPageHeader';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import {
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
  ANY_SPORT_MY_CLUB_SLUG_CARDS_TYPE,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS,
  ANY_SPORT_MY_CLUB_SLUG_VAULT,
  ANY_SPORT_MY_CLUB_SLUG_VAULT_TYPE,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { matchAnySportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import type {
  MyClubSlugLayoutQuery,
  MyClubSlugLayoutQueryVariables,
} from './__generated__/index.graphql';

const MY_CLUB_SLUG_QUERY = gql`
  query MyClubSlugLayoutQuery($slug: String!) {
    user(slug: $slug) {
      slug
      nickname
      ...MyClubPageHeader_user
    }
  }
  ${MyClubPageHeader.fragments.user}
` as TypedDocumentNode<MyClubSlugLayoutQuery, MyClubSlugLayoutQueryVariables>;

const MyClubTabs = ({
  slug,
  type,
}: {
  type: Nullable<Exclude<Rarity, typeof Rarity.custom_series>>;
  slug: string;
}) => {
  const location = useLocation();
  const { generateSportContextPath } = useSportContext();

  const showTabs = [
    ANY_SPORT_MY_CLUB_SLUG_CARDS,
    ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS,
    ANY_SPORT_MY_CLUB_SLUG_VAULT,
  ].some(path => !!matchAnySportPath(catchAll(path), location.pathname));

  if (!showTabs) {
    return null;
  }
  return (
    <OnboardingHighlightWrapper highlightName="CollectionNavigationSwitch">
      <TabBar fullWidth variant="flat">
        <Tab
          to={
            type
              ? generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS_TYPE, {
                  params: { slug, type },
                })
              : generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS, {
                  params: { slug },
                })
          }
        >
          <FormattedMessage {...navLabels.gallery} />
        </Tab>
        <Tab
          to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS, {
            params: { slug },
          })}
        >
          <FormattedMessage {...navLabels.collections} />
        </Tab>
        <Tab
          to={
            type && type !== Rarity.common
              ? generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_VAULT_TYPE, {
                  params: { slug, type },
                })
              : generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_VAULT, {
                  params: { slug },
                })
          }
        >
          <FormattedMessage {...navLabels.vault} />
        </Tab>
      </TabBar>
    </OnboardingHighlightWrapper>
  );
};

export const loader = queryLoader<{ slug: string }>(
  MY_CLUB_SLUG_QUERY,
  ({ slug }) => ({
    slug,
  })
);

export const MyClubSlugLayout = () => {
  const { data } = useQueryLoader<MyClubSlugLayoutQuery>();
  return (
    <>
      <MyClubPageHeader user={data.user} />
      <MyClubTabs slug={data.user.slug} type={null} />
      <Outlet />
    </>
  );
};
